// Layer 3 seed (static): Bunker Hill's two `ConcentrateSpec` rows —
// paul-fmea.A15 / #280. Hand-transcribed once from
// `docs/meetings/inputs/2026-09-09-paul-fmea-v8.md` § Penalty element tab
// (typical-trigger bands, five tables on the sheet, two in scope for a
// Pb-Zn-Ag concentrator) and § Section targets (grade specs), joined to the
// existing deck-sourced contract limits already seeded on `rd-spec-pb-as` /
// `rd-spec-zn-as` (`bunker-hill-readings.ts`). See `concentrate-spec.ts` for
// why the two threshold kinds stay distinct.

import type { ConcentrateProduct, ConcentrateSpec } from '@/versions/v1/schema/concentrate-spec';

const V8_TRIGGER_PB =
  "Paul's V8 workbook, Penalty Element tab — Lead Concentrate, Typical Trigger column (industry-typical range, not this plant's contract)";
const V8_TRIGGER_ZN =
  "Paul's V8 workbook, Penalty Element tab — Zinc Concentrate, Typical Trigger column (industry-typical range, not this plant's contract)";
const CONTRACT_PROVENANCE = 'Deck slide 8 smelter contract limits';
const ILLUSTRATIVE_SCHEDULE = 'illustrative smelter schedule — see decisions.md § Bound by Miles, Money is seeded';

export const CONCENTRATE_SPECS: readonly ConcentrateSpec[] = [
  {
    id: 'cs-pb',
    product: 'Pb',
    streamId: 'st-pbcon',
    label: 'Pb concentrate',
    gradeSpecs: [
      {
        parameter: 'Pb grade',
        lo: 67,
        unit: '% Pb',
        readingId: 'rd-spec-pb-grade',
        provenance: "Paul's V8 workbook § Section targets — \"Pb Con Grade >67% Pb\"",
      },
      {
        parameter: 'Ag content',
        unit: 'oz/t',
        readingId: 'rd-spec-ag-grade',
        // K20 (#717): 2 dp, matching the reading's own `decimals` and the
        // balance's product grade (12.14 oz/t); 1 dp truncated it to "12.1".
        displayDecimals: 2,
        provenance:
          'Module doc §8.4 design basis — no separate V8 penalty-tab target names Ag content',
      },
    ],
    penaltyElements: [
      {
        element: 'As',
        contractLimit: { hi: 0.5, unit: '%', readingId: 'rd-spec-pb-as', provenance: CONTRACT_PROVENANCE },
        typicalTrigger: { lo: 0.2, hi: 0.3, unit: '%', decimals: 1, provenance: V8_TRIGGER_PB },
        assayed: true,
        causeId: 'MD-053',
      },
      {
        element: 'Sb',
        contractLimit: { hi: 1.5, unit: '%', readingId: 'rd-spec-pb-sb', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 0.5, hi: 1.0, unit: '%', decimals: 1, provenance: V8_TRIGGER_PB },
        assayed: true,
        causeId: 'MD-054',
      },
      {
        element: 'Bi',
        contractLimit: { hi: 0.15, unit: '%', readingId: 'rd-spec-pb-bi', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 0.05, hi: 0.1, unit: '%', decimals: 2, provenance: V8_TRIGGER_PB },
        assayed: true,
        causeId: 'MD-055',
      },
      {
        element: 'Zn',
        contractLimit: { hi: 5, unit: '%', readingId: 'rd-pb-con-zn', provenance: ILLUSTRATIVE_SCHEDULE },
        typicalTrigger: { lo: 3, hi: 5, unit: '%', decimals: 0, provenance: V8_TRIGGER_PB },
        assayed: true,
      },
      {
        element: 'Hg',
        contractLimit: { hi: 15, unit: 'ppm', readingId: 'rd-spec-pb-hg', provenance: ILLUSTRATIVE_SCHEDULE },
        typicalTrigger: { hi: 10, unit: 'ppm', decimals: 0, provenance: V8_TRIGGER_PB },
        assayed: true,
      },
    ],
    provenance: "docs/meetings/inputs/2026-09-09-paul-fmea-v8.md § Penalty element tab — Lead Concentrate",
  },
  {
    id: 'cs-zn',
    product: 'Zn',
    streamId: 'st-zncon',
    label: 'Zn concentrate',
    gradeSpecs: [
      {
        parameter: 'Zn grade',
        lo: 55,
        unit: '% Zn',
        readingId: 'rd-spec-zn-grade',
        provenance: "Paul's V8 workbook § Section targets — \"Zn Con Grade >55% Zn\"",
      },
    ],
    penaltyElements: [
      {
        element: 'Hg',
        contractLimit: { hi: 15, unit: 'ppm', readingId: 'rd-spec-zn-hg', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 5, hi: 10, unit: 'ppm', decimals: 0, provenance: V8_TRIGGER_ZN },
        assayed: true,
      },
      {
        element: 'F',
        contractLimit: { hi: 400, unit: 'ppm', readingId: 'rd-spec-zn-f', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 200, hi: 300, unit: 'ppm', decimals: 0, provenance: V8_TRIGGER_ZN },
        assayed: true,
        causeId: 'MD-059',
      },
      {
        element: 'Cl',
        contractLimit: { hi: 0.08, unit: '%', readingId: 'rd-spec-zn-cl', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 0.03, hi: 0.05, unit: '%', decimals: 2, provenance: V8_TRIGGER_ZN },
        assayed: true,
        causeId: 'MD-059',
      },
      {
        element: 'Cd',
        contractLimit: { hi: 0.5, unit: '%', readingId: 'rd-spec-zn-cd', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 0.2, hi: 0.3, unit: '%', decimals: 1, provenance: V8_TRIGGER_ZN },
        assayed: true,
        causeId: 'MD-057',
      },
      {
        element: 'As',
        contractLimit: { hi: 0.1, unit: '%', readingId: 'rd-spec-zn-as', provenance: CONTRACT_PROVENANCE },
        typicalTrigger: { lo: 0.1, hi: 0.2, unit: '%', decimals: 1, provenance: V8_TRIGGER_ZN },
        assayed: true,
      },
      {
        element: 'Sb',
        contractLimit: { hi: 0.08, unit: '%', readingId: 'rd-spec-zn-sb', provenance: '[syn] month-six assay' },
        typicalTrigger: { lo: 0.02, hi: 0.05, unit: '%', decimals: 2, provenance: V8_TRIGGER_ZN },
        assayed: true,
        causeId: 'MD-054',
      },
    ],
    provenance: "docs/meetings/inputs/2026-09-09-paul-fmea-v8.md § Penalty element tab — Zinc Concentrate",
  },
];

export function concentrateSpecByProduct(product: ConcentrateProduct): ConcentrateSpec {
  const spec = CONCENTRATE_SPECS.find((row) => row.product === product);
  if (!spec) throw new Error(`No ConcentrateSpec seeded for product ${product}`);
  return spec;
}

/** This plant's smelter contract limit for arsenic on one concentrate — the
 *  single source every literal `0.5` / `0.1` on Quality › Impurities used to
 *  duplicate (review M11). Reading it here means a change to the
 *  deck-sourced limit moves every figure derived from it. */
export function arsenicContractLimit(product: ConcentrateProduct): number {
  const spec = concentrateSpecByProduct(product);
  const hi = spec.penaltyElements.find((row) => row.element === 'As')?.contractLimit?.hi;
  if (hi === undefined) throw new Error(`No As contract limit seeded for ${product}`);
  return hi;
}
